import React, { useState, useEffect } from 'react';
import { FaMapMarkerAlt, FaArrowRight } from 'react-icons/fa';
import { getBlogArticles } from '../api/client';

/**
 * Related-articles block for the foot of an article.
 *
 * A reader who finished a piece has to have somewhere to go next; without this
 * the article is a dead end. Related articles are drawn from the same
 * destination first (BlogArticle.destination is an indexed column), then topped
 * up from the same category so the block still fills for one-off destinations.
 */
const RelatedArticles = ({ slug, destination, category, onViewArticle, limit = 3 }) => {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const collected = [];
      const seen = new Set([slug]);

      const absorb = (data) => {
        (data?.articles || []).forEach((a) => {
          if (seen.has(a.slug) || collected.length >= limit) return;
          seen.add(a.slug);
          collected.push(a);
        });
      };

      try {
        if (destination) {
          absorb(await getBlogArticles({ destination, limit: limit + 1 }));
        }
        if (collected.length < limit && category) {
          absorb(await getBlogArticles({ category, limit: limit + 1 }));
        }
        if (collected.length < limit) {
          absorb(await getBlogArticles({ limit: limit + 1 }));
        }
      } catch {
        // A failed related-articles fetch must never blank the article footer.
      }

      if (!cancelled) setArticles(collected);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [slug, destination, category, limit]);

  if (articles.length === 0) return null;

  const openArticle = (e, articleSlug) => {
    if (onViewArticle) {
      e.preventDefault();
      onViewArticle(articleSlug);
      window.scrollTo(0, 0);
    }
  };

  const hubHref = destination
    ? `/blog?destination=${encodeURIComponent(destination)}`
    : '/blog';

  return (
    <section className="mt-12 pt-8 border-t border-neutral-200">
      <div className="flex items-baseline justify-between gap-4 mb-5 flex-wrap">
        <h2 className="font-serif text-xl font-bold text-neutral-900">
          {destination ? `Keep reading about ${destination}` : 'Keep reading'}
        </h2>
        {/* Full navigation is deliberate here — BlogList reads ?destination=
            from the URL on load. */}
        <a
          href={hubHref}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 inline-flex items-center gap-1"
        >
          {destination ? `All ${destination} guides` : 'All travel guides'}
          <FaArrowRight className="text-xs" />
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {articles.map((a) => (
          <a
            key={a.id || a.slug}
            href={`/blog/${a.slug}`}
            onClick={(e) => openArticle(e, a.slug)}
            className="group block bg-white rounded-xl border border-neutral-200 overflow-hidden hover:shadow-lg hover:border-primary-300 transition-all no-underline"
          >
            {a.featured_image && (
              <div className="h-32 overflow-hidden">
                <img
                  src={a.featured_image}
                  alt={a.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            )}
            <div className="p-4">
              {a.destination && (
                <span className="text-xs text-neutral-500 flex items-center gap-1 mb-1.5">
                  <FaMapMarkerAlt className="text-[10px]" /> {a.destination}
                </span>
              )}
              <h3 className="text-[0.95rem] font-semibold text-neutral-800 leading-snug group-hover:text-primary-600 transition-colors line-clamp-3">
                {a.title}
              </h3>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
};

export default RelatedArticles;
